import { useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useWizardFilterContext } from 'components/Wizards/MetaFilters/context';

import {
  AddFilterButton,
  AddFilterSetButton,
  Filter,
  FilterOperatorText,
  FilterRow,
  FilterSetContainer,
  FilterSetOperationDropdown,
  useApiFilterErrors,
  useMappedReturnErrors,
} from 'ts-components/filters';

export const FilterSets = ({ hideOutOfBoundaries = false }) => {
  const { t } = useTranslation();

  const {
    filterErrors,
    operation,
    setOperation,
    setHasErrors,
    filterSets,
    filterOps: { addFilter, addFilterSet, removeFilter, setFilterSetOperation },
    isForCustomObject,
    customObject,
  } = useWizardFilterContext();

  const clearFilters = useCallback(() => {
    setHasErrors(false);
    // will need to clear api errors as well
  }, [setHasErrors]);

  const objectType = isForCustomObject
    ? customObject.objectType
    : customObject?.id
      ? 'client_client'
      : null;

  const errorsReturned = null;

  const errorsDictionary = useMappedReturnErrors(errorsReturned, filterSets);
  const [apiErrors] = useApiFilterErrors(errorsDictionary, 3000);

  const multipleSets = filterSets.length > 1;

  return (
    <>
      {filterSets.map(([setId, { and, filters }], setIdx) => {
        const multipleFilters = filters.length > 1;
        return (
          <Fragment key={setId}>
            {setIdx > 0 && (
              <FilterSetOperationDropdown
                operation={operation}
                onChange={setOperation}
              />
            )}
            <FilterSetContainer
              value={and ? 'all' : 'any'}
              showBorder={multipleFilters || multipleSets}
              onChange={(value) =>
                setFilterSetOperation(setId, value === 'all')
              }
            >
              {filters.map((filter, idx, arr) => {
                const { type, steps, ops, id, tooltip } = filter;
                const isLastFilter = idx === arr.length - 1;
                const filterTypeChosen = type !== null;
                const errors = filterErrors[setId]?.[idx] ?? {};

                if (apiErrors[setId]?.[idx]) {
                  errors.filter_type = t('This filter is invalid.');
                }

                return (
                  <FilterRow key={id}>
                    <Filter
                      objectType={objectType}
                      showDeleteIcon={multipleFilters || multipleSets}
                      tooltip={tooltip}
                      steps={steps}
                      next={ops.next}
                      set={ops.set}
                      errors={errors}
                      modalLayer={2} // must support nested modals since edit filter modal can exist in another modal
                      translateY={-1}
                      hideOutOfBoundaries={hideOutOfBoundaries}
                      onChange={() => {
                        clearFilters();
                        ops.update();
                      }}
                      onDelete={() => {
                        removeFilter(setId, idx);
                      }}
                    />
                    {arr.length > 1 && !isLastFilter && (
                      <FilterOperatorText and={and} />
                    )}
                    {filterTypeChosen && isLastFilter && (
                      <AddFilterButton onClick={() => addFilter(setId)} />
                    )}
                  </FilterRow>
                );
              })}
            </FilterSetContainer>
          </Fragment>
        );
      })}
      <AddFilterSetButton
        topMargin={multipleSets || filterSets[0][1].filters.length > 1}
        onClick={(value) => {
          addFilterSet();
          if (!multipleSets) {
            setOperation(value);
          }
        }}
      />
    </>
  );
};
